/**
 * @mercnet/windmill - Windmill integration adapter for MercNet GraphQL API
 *
 * This package provides a simplified interface for Windmill workflows to interact
 * with the MercNet GraphQL API through a pre-configured client.
 */

import {
  createGraphQLClient,
  type MercNetGraphQLClient,
  type GraphQLClientConfig,
  type Market
} from '@mercnet/core'

/**
 * Configuration options for the Windmill MercNet client
 */
export interface WindmillClientConfig {
  /** GraphQL endpoint URL */
  endpoint: string
  /** Authentication token for API access */
  token?: string
  /** Additional headers to include in requests */
  headers?: Record<string, string>
  /** Number of retry attempts for failed requests */
  retries?: number
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Default configuration values
 */
const DEFAULT_WINDMILL_CONFIG: Partial<WindmillClientConfig> = {
  retries: 3,
  debug: false
}

/**
 * Creates a pre-configured MercNet GraphQL client for use in Windmill workflows
 *
 * @param config - Configuration options for the client
 * @returns Configured MercNetGraphQLClient instance
 *
 * @example
 * ```typescript
 * // Basic usage
 * const client = createClient({
 *   endpoint: 'https://graphql.rocketeer.trade/',
 *   token: 'your-auth-token'
 * })
 *
 * // Get all markets
 * const markets = await client.getAllMarkets()
 *
 * // Get specific market
 * const market = await client.getMarketById(1)
 * ```
 */
export function createClient(config: WindmillClientConfig): MercNetGraphQLClient {
  const mergedConfig: WindmillClientConfig = {
    ...DEFAULT_WINDMILL_CONFIG,
    ...config
  }

  // Build headers with authentication
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(mergedConfig.headers || {})
  }

  // Add authorization header if token is provided
  if (mergedConfig.token) {
    headers['Authorization'] = `Bearer ${mergedConfig.token}`
  }

  // Create GraphQL client configuration
  const graphqlConfig: GraphQLClientConfig = {
    endpoint: mergedConfig.endpoint,
    headers,
    retries: mergedConfig.retries || 3,
    debug: mergedConfig.debug || false
  }

  return createGraphQLClient(graphqlConfig)
}

/**
 * Convenience function to create a client with default MercNet endpoint
 *
 * @param config - Partial configuration (endpoint is pre-filled)
 * @returns Configured MercNetGraphQLClient instance
 *
 * @example
 * ```typescript
 * const client = createMercNetClient({
 *   token: 'your-auth-token'
 * })
 * ```
 */
export function createMercNetClient(
  config: Omit<WindmillClientConfig, 'endpoint'> & { endpoint?: string }
): MercNetGraphQLClient {
  return createClient({
    endpoint: 'https://graphql.rocketeer.trade/',
    ...config
  })
}

/**
 * Windmill-specific utilities for common trading operations
 */
export class WindmillMercNetUtils {
  constructor(private client: MercNetGraphQLClient) {}

  /**
   * Get active markets with basic filtering
   */
  async getActiveMarkets(): Promise<
    Array<{
      id: number
      name: string
      ticker: string
      price: string | null
      status: string | null
    }>
  > {
    const markets = await this.client.getAllMarkets()

    return markets
      .filter((market: Market) => market.status === 'active')
      .map((market: Market) => ({
        id: market.id,
        name: market.name || 'Unknown',
        ticker: market.ticker,
        price: market.price || null,
        status: market.status || null
      }))
  }

  /**
   * Get market summary for reporting
   */
  async getMarketSummary(marketId: number): Promise<{
    id: number
    name: string
    ticker: string
    price: string | null
    sentiment: string | null
    trend: string | null
    lastUpdate: string | null
    activityScore1h: string | null
    activityScore1d: string | null
  } | null> {
    const market = await this.client.getMarketById(marketId)

    if (!market) {
      return null
    }

    return {
      id: market.id,
      name: market.name || 'Unknown',
      ticker: market.ticker,
      price: market.price || null,
      sentiment: market.sentiment || null,
      trend: market.trend_status || null,
      lastUpdate: market.last_update_at || null,
      activityScore1h: market.market_activity_score_1h || null,
      activityScore1d: market.market_activity_score_1d || null
    }
  }

  /**
   * Search markets by ticker pattern
   */
  async searchMarketsByTicker(tickerPattern: string): Promise<
    Array<{
      id: number
      name: string
      ticker: string
      price: string | null
    }>
  > {
    const allMarkets = await this.client.getAllMarkets()

    const pattern = tickerPattern.toLowerCase()
    return allMarkets
      .filter((market: Market) => market.ticker.toLowerCase().includes(pattern))
      .map((market: Market) => ({
        id: market.id,
        name: market.name || 'Unknown',
        ticker: market.ticker,
        price: market.price || null
      }))
  }

  /**
   * Get markets with sentiment analysis
   */
  async getMarketsSentiment(): Promise<
    Array<{
      id: number
      ticker: string
      sentiment: string | null
      sentimentUpdateAt: string | null
      trendStatus: string | null
    }>
  > {
    const allMarkets = await this.client.getAllMarkets()

    return allMarkets
      .filter((market: Market) => market.sentiment !== null)
      .map((market: Market) => ({
        id: market.id,
        ticker: market.ticker,
        sentiment: market.sentiment || null,
        sentimentUpdateAt: market.sentiment_update_at || null,
        trendStatus: market.trend_status || null
      }))
  }
}

/**
 * Create a WindmillMercNetUtils instance with a pre-configured client
 *
 * @param config - Configuration for the client
 * @returns WindmillMercNetUtils instance
 *
 * @example
 * ```typescript
 * const utils = createUtils({
 *   endpoint: 'https://graphql.rocketeer.trade/',
 *   token: 'your-token'
 * })
 *
 * const activeMarkets = await utils.getActiveMarkets()
 * ```
 */
export function createUtils(config: WindmillClientConfig): WindmillMercNetUtils {
  const client = createClient(config)
  return new WindmillMercNetUtils(client)
}

/**
 * Re-export types and utilities from core package for convenience
 */
export type {
  Market,
  GraphQLResponse,
  GraphQLError,
  MercNetGraphQLClient,
  GraphQLClientConfig
} from '@mercnet/core'

export { MercNetError, createGraphQLClient } from '@mercnet/core'

/**
 * Export types specific to this package
 * WindmillClientConfig is already exported as interface above
 */
